import { useMemo } from "react";
import { collection, query, where, orderBy } from "firebase/firestore";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Lock, User as UserIcon, X } from "lucide-react";
import { db } from "../lib/firebase";
import { useFirestoreCollectionOnce } from "../hooks/useFirestoreCollectionOnce";
import { useEventParticipants } from "../hooks/useEventParticipants";
import type { Audience, Person, Role } from "@event-planner/shared";
import { personalAudienceId } from "@event-planner/shared";

/**
 * Audience picker for parent compose forms (ADR-120, ADR-136).
 *
 * Combines two sources of audience IDs into one multi-select:
 *
 *   - Named audiences: custom groups + role audiences for this event.
 *     Picked directly by audience ID.
 *   - Event participants: every active + pending participant in the
 *     event. Picked by display name; the picker emits the person's
 *     personal audience ID (`aud_personal_{eventId}_{personId}`).
 *
 * Under ADR-136 the legacy `contact:{id}` pseudo-ID machinery is gone:
 * pending participants are real persons with their own
 * `eventParticipants` row and personal audience, so they surface
 * uniformly alongside claimed participants. Parent forms read a flat
 * `string[]` of audience IDs and call `resolveAudienceTarget` to compose
 * them.
 *
 * Emits `pickedAudienceIds: string[]` to the form.
 */

const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  organizer: "Organizer",
  internal: "Internal Team",
  invitee: "Invitee",
  companion: "Companion",
};

function describeRoleAudience(audience: Audience): string {
  const roles = audience.members.roles;
  if (roles.length >= 5) return "Everyone on this event.";
  if (roles.length === 1) {
    return `Everyone with role: ${ROLE_LABELS[roles[0]]}.`;
  }
  const labels = roles.map((r) => ROLE_LABELS[r]).join(", ");
  return `Everyone with roles: ${labels}.`;
}

function personSecondary(person: Person): string | undefined {
  return (
    [person.publicTitle, person.publicOrganization]
      .filter(Boolean)
      .join(" · ") || undefined
  );
}

interface AudienceOption {
  id: string;
  label: string;
  /**
   * "audience" = named audience reference; "person" = personal audience
   * reference for any participant (claimed or pending).
   */
  kind: "audience" | "person";
  /** Set when kind === 'audience' and the audience is a role audience. */
  roleAudience?: Audience;
  /** Set when kind === 'person'. */
  person?: Person;
}

interface AudiencePickerProps {
  eventId: string | undefined;
  value: string[];
  onChange: (next: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  optionGroupLabel?: string;
  excludedIds?: string[];
  /**
   * Include pending event participants so organizers can target people
   * before first sign-in. Under ADR-136 pending people are real persons
   * with status='pending' on `eventParticipants`; the picker surfaces
   * them by their personal audience ID, no staging required.
   */
  includePendingContacts?: boolean;
  disabled?: boolean;
  testId?: string;
  required?: boolean;
}

export function AudiencePicker({
  eventId,
  value,
  onChange,
  label = "Audience",
  placeholder = "Pick groups or people…",
  helperText,
  optionGroupLabel = "Groups",
  excludedIds = [],
  includePendingContacts = true,
  disabled = false,
  testId,
  required,
}: AudiencePickerProps) {
  // Named audiences only — `name == null` (anonymous + personal) are
  // filtered out at the picker layer (they're not user-discoverable).
  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
            orderBy("name", "asc"),
          )
        : null,
    [eventId],
  );

  const {
    data: rawAudiences,
    loading: audiencesLoading,
    refetch: refetchAudiences,
  } = useFirestoreCollectionOnce<Audience>(audiencesQuery);

  const namedAudiences = useMemo(
    () => rawAudiences.filter((a) => a.name !== null),
    [rawAudiences],
  );

  // Pending event participants are real people under ADR-136. Include them
  // by default so every targeting surface can resolve personal audiences
  // before first sign-in. Rare claimed-only pickers can opt out.
  const participantStatuses = useMemo(
    () =>
      includePendingContacts
        ? (["active", "pending"] as const)
        : (["active"] as const),
    [includePendingContacts],
  );
  const {
    persons,
    loading: participantsLoading,
    refetch: refetchParticipants,
  } = useEventParticipants(eventId, [...participantStatuses]);

  const excludedIdSet = useMemo(() => new Set(excludedIds), [excludedIds]);

  const options: AudienceOption[] = useMemo(() => {
    if (!eventId) return [];
    const systemAuds = namedAudiences
      .filter((a) => a.system)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    const customAuds = namedAudiences
      .filter((a) => !a.system)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

    const audOptions: AudienceOption[] = [...systemAuds, ...customAuds].map(
      (a) => ({
        id: a.id,
        label: a.name ?? a.id,
        kind: "audience",
        roleAudience: a.system ? a : undefined,
      }),
    );

    const personOptions: AudienceOption[] = persons
      .slice()
      .sort((a, b) => {
        const last = (a.lastName ?? "").localeCompare(b.lastName ?? "");
        if (last !== 0) return last;
        return (a.firstName ?? "").localeCompare(b.firstName ?? "");
      })
      .map((p) => ({
        id: personalAudienceId(eventId, p.id),
        label: p.displayName || p.email,
        kind: "person",
        person: p,
      }));

    return [...audOptions, ...personOptions].filter(
      (option) => !excludedIdSet.has(option.id),
    );
  }, [namedAudiences, persons, eventId, excludedIdSet]);

  const optionById = useMemo(
    () => new Map(options.map((o) => [o.id, o])),
    [options],
  );

  const optionIds = useMemo(() => options.map((o) => o.id), [options]);

  const selectedGroupRows = useMemo(
    () =>
      value
        .map((id) => ({ id, option: optionById.get(id) }))
        .filter((row) => row.option?.kind === "audience"),
    [optionById, value],
  );

  // Resolved people rows for the right pane. Walks audience-by-id and
  // role membership to surface every person included by the current
  // value selection, deduped.
  const includedPeopleRows = useMemo(() => {
    if (!eventId) return [];
    const audienceById = new Map(rawAudiences.map((a) => [a.id, a]));
    const personById = new Map(persons.map((p) => [p.id, p]));
    const includedPersonIds = new Set<string>();
    // For each selected audience walk its resolvedPersonIds (CF-derived,
    // includes pending + claimed). Personal audiences carry a single
    // personId; named audiences carry the full resolved set.
    for (const id of value) {
      const option = optionById.get(id);
      if (option?.kind === "person" && option.person) {
        includedPersonIds.add(option.person.id);
        continue;
      }

      const audience = audienceById.get(id);
      if (!audience) continue;
      for (const personId of audience.resolvedPersonIds ?? []) {
        includedPersonIds.add(personId);
      }
    }

    type Row = {
      id: string;
      label: string;
      secondary: string | undefined;
      photoURL: string | null;
      initial: string;
      /** The audience ID emitted by the picker for this row, if directly
       * selected — null if the row is only included transitively. */
      removeValue: string | null;
    };

    // Map personId to the picker value that directly selected them (if
    // any) so the per-row X button can remove just that selection.
    const directSelectedPersonalAudByPersonId = new Map<string, string>();
    for (const id of value) {
      const option = optionById.get(id);
      if (option?.kind === "person" && option.person) {
        directSelectedPersonalAudByPersonId.set(option.person.id, id);
        continue;
      }

      const audience = audienceById.get(id);
      if (
        audience &&
        audience.system &&
        audience.name === null &&
        audience.members.personIds.length === 1
      ) {
        directSelectedPersonalAudByPersonId.set(
          audience.members.personIds[0],
          id,
        );
      }
    }

    return Array.from(includedPersonIds)
      .map((personId) => personById.get(personId))
      .filter((p): p is Person => Boolean(p))
      .sort((a, b) =>
        (a.lastName ?? "").localeCompare(b.lastName ?? "") ||
        (a.firstName ?? "").localeCompare(b.firstName ?? ""),
      )
      .map((p): Row => ({
        id: `person:${p.id}`,
        label: p.displayName || p.email,
        secondary: personSecondary(p),
        photoURL: p.photoURL,
        initial: (p.firstName ?? p.displayName ?? p.email)[0] ?? "?",
        removeValue:
          directSelectedPersonalAudByPersonId.get(p.id) ?? null,
      }));
  }, [eventId, persons, rawAudiences, value, optionById]);

  const loading = audiencesLoading || participantsLoading;

  return (
    <Box>
      <Autocomplete
        multiple
        options={optionIds}
        value={value}
        onChange={(_, next) => onChange(next)}
        onOpen={() => {
          refetchAudiences();
          refetchParticipants();
        }}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(id) => optionById.get(id)?.label ?? id}
        isOptionEqualToValue={(opt, val) => opt === val}
        noOptionsText="No matching people or groups"
        groupBy={(id) => {
          const opt = optionById.get(id);
          if (opt?.kind === "person") return "People";
          return optionGroupLabel;
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader
              component="div"
              disableSticky
              sx={{
                lineHeight: "32px",
                fontSize: 12,
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              {params.group}
            </ListSubheader>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        )}
        renderOption={(props, id) => {
          const opt = optionById.get(id);
          const { key, ...rest } = props as { key?: string } & Record<
            string,
            unknown
          >;
          return (
            <li key={key ?? id} {...rest}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {opt?.kind === "person" && (
                  <UserIcon size={14} aria-label="Person" />
                )}
                {opt?.roleAudience && (
                  <Tooltip title={describeRoleAudience(opt.roleAudience)}>
                    <Lock size={14} aria-label="Built-in group" />
                  </Tooltip>
                )}
                <Box component="span">
                  {opt?.label ?? id}
                </Box>
              </Box>
            </li>
          );
        }}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={helperText}
            required={required}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "data-testid": testId,
              },
            }}
          />
        )}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 0.34fr) 1fr" },
          gap: 3,
          mt: 1.5,
          maxHeight: 320,
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Groups ({selectedGroupRows.length})
          </Typography>
          {selectedGroupRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No groups selected.
            </Typography>
          ) : (
            <List dense disablePadding>
              {selectedGroupRows.map(({ id, option }) => (
                <ListItem key={id} disableGutters sx={{ py: 0.25 }}>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{ width: 28, height: 28 }}>
                      {option?.roleAudience ? <Lock size={14} /> : "G"}
                    </Avatar>
                  </ListItemAvatar>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {option?.label ?? id}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${option?.label ?? id}`}
                      onClick={() => onChange(value.filter((v) => v !== id))}
                    >
                      <X size={14} />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5 }}
            data-testid="audience-picker-included-people-header"
          >
            People ({includedPeopleRows.length})
          </Typography>
          {includedPeopleRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No people selected yet.
            </Typography>
          ) : (
            <List
              dense
              disablePadding
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "repeat(2, minmax(260px, 1fr))",
                },
                columnGap: 2,
                rowGap: 0.25,
              }}
            >
              {includedPeopleRows.map((row) => (
                <ListItem
                  key={row.id}
                  disableGutters
                  sx={{ py: 0.35 }}
                  data-testid={row.id.replace(
                    /^person:/,
                    "audience-picker-included-person-",
                  )}
                >
                  <ListItemAvatar sx={{ minWidth: 46 }}>
                    <Avatar
                      src={row.photoURL ?? undefined}
                      alt={row.label}
                      sx={{ width: 36, height: 36 }}
                    >
                      {row.initial}
                    </Avatar>
                  </ListItemAvatar>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          component="span"
                          sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}
                        >
                          <span>{row.label}</span>
                        </Box>
                      }
                      secondary={row.secondary}
                      sx={{ my: 0 }}
                    />
                    {row.removeValue && (
                      <IconButton
                        size="small"
                        aria-label={`Remove ${row.label}`}
                        onClick={() =>
                          onChange(value.filter((v) => v !== row.removeValue))
                        }
                      >
                        <X size={14} />
                      </IconButton>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Box>
  );
}
